import React, { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { useClipboard } from 'use-clipboard-copy'

import CloseIcon from '@mui/icons-material/Close'
import { Button, TextField, Tooltip, Typography } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import withStyles from '@mui/styles/withStyles'

import { ErrorAlert } from 'components/core/Alert'
import { finishAcquiringCredentialsRegenerate, startAcquiringCredentialsRegenerate } from 'redux/acquiring/acquiring-actions'
import { useAppSelector } from 'redux/hooks'
import { RootState } from 'redux/store'
import { IMAGES_PATH, themeStyles } from 'themes'
import combineStyles from 'utils/combineStyles'

import { COMPONENT_EXTENDER } from 'extender'

const copyIcon = IMAGES_PATH('/copy-icon.svg')

const TwoFAVerificationComponent = COMPONENT_EXTENDER('components/core/2FA/2FA-component')

interface IProps {
    classes: any
    handleClose: () => void
}

const AcquiringApiRegenerateModal: FC<IProps> = ({ classes, handleClose }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [step, setStep] = useState(1)
    const [publicKey, setPublicKey] = useState('')
    const [clipboardContent, setClipboardContent] = useState('')

    const { success, errorMessage, cardAcquiringApiKey } = useAppSelector((state: RootState) => state.acquiring)

    useEffect(() => {
        if (success?.ACQUIRING_CREDENTIALS_REGENERATE) {
            setStep(3)
        }
    }, [success?.ACQUIRING_CREDENTIALS_REGENERATE])

    useEffect(() => {
        if (errorMessage?.[0]?.parameter === 'PublicKey') {
            setStep(1)
        }
    }, [errorMessage])

    useEffect(
        () => () => {
            dispatch(finishAcquiringCredentialsRegenerate())
        },
        []
    )

    const handleStep = () => {
        setStep(prevState => prevState + 1)
    }

    const handleCodeInput = useCallback(
        (verificationCode: any) => {
            if (verificationCode.length >= 6) {
                dispatch(startAcquiringCredentialsRegenerate({ publicKey, verificationCode }))
            }
        },
        [publicKey]
    )

    const clipboard = useClipboard({
        copiedTimeout: 2000,
    })

    const handleClipboardClick = value => {
        setClipboardContent(String(value))
        clipboard.copy(value)
    }

    return (
        <div>
            <div className={classes.modalFixedHeader}>
                <Typography data-sta-id="add-money-form-h1-title" variant="h1">
                    {t('Regenerate secrets')}
                </Typography>
                <IconButton
                    data-sta-id="add-money-form-button-close"
                    aria-label="close"
                    className={classes.modalCloseButton}
                    onClick={handleClose}
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
            </div>
            <div className={classes.modalContent}>
                <ErrorAlert />
                <div style={{ textAlign: 'center', marginBottom: 20 }}>{t('Acquiring.ClientKeyWarning')}</div>
                {step === 1 && (
                    <>
                        <TextField
                            style={{ marginBottom: 20 }}
                            label={t('Acquiring.PublicKey')}
                            type="text"
                            variant="outlined"
                            margin="none"
                            fullWidth
                            multiline
                            rows="12"
                            autoComplete="none"
                            required
                            onChange={e => {
                                setPublicKey(e.target.value)
                            }}
                            value={publicKey || ''}
                        />
                        <Button
                            onClick={handleStep}
                            className={classes.buttonBig}
                            variant="contained"
                            color="primary"
                            disabled={!publicKey.length}
                        >
                            {t('Acquiring.Regenerate')}
                        </Button>
                    </>
                )}
                {step === 2 && <TwoFAVerificationComponent handleClose={handleClose} sendCode callback={handleCodeInput} />}
                {step === 3 && (
                    <>
                        <div style={{ textAlign: 'center', marginBottom: 20 }}>{t('Acquiring.ClientKeyCopy')}</div>
                        <div style={{ textAlign: 'center', fontSize: 18 }}>
                            <span style={{ marginRight: '8px' }}>{cardAcquiringApiKey}</span>
                            <Tooltip
                                title={
                                    clipboard.copied && clipboardContent === cardAcquiringApiKey
                                        ? `${t('PersonalProfile.Copied')}`
                                        : `${t('PersonalProfile.Copy')}`
                                }
                                placement="left"
                                data-sta-id="acquiring-client-key-copy"
                                arrow={true}
                                // open={clipboard.copied && clipboardContent === accountNumber}
                                enterDelay={0}
                                enterTouchDelay={0}
                            >
                                <span onClick={() => handleClipboardClick(cardAcquiringApiKey)}>
                                    <img src={copyIcon} alt={cardAcquiringApiKey} style={{ cursor: 'pointer' }} />
                                </span>
                            </Tooltip>
                            <input ref={clipboard.target} value={cardAcquiringApiKey || ''} hidden readOnly />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default withStyles(combineStyles(themeStyles.useModalStyles, themeStyles.useAcquiringStyles, themeStyles.useTableStyles))(
    AcquiringApiRegenerateModal
)
